import type { CityDto, GeoCoords } from '@/core/data/location/location.interface';
import { localizeWithValues } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import { useCityStore } from '@/stores/city.store';
import { useNavigationStore } from '@/stores/navigation.store';
import { useSparkIntentStore } from '@/stores/spark.intent';
import { useSparkStore } from '@/stores/spark.store';
import ConfirmDialog from '@/views/main/sparks/modals/ConfirmDialog.vue';
import { storeToRefs } from 'pinia';
import { useModal } from 'vue-final-modal';

export interface CitySwitchTarget {
  coordinates: GeoCoords;
  name: string;
}

export interface UseCitySwitchFlowOptions {
  onComplete?: () => void;
  onCancel?: () => void;
}

export function isOutsidePlayingCity(coords: GeoCoords, city: CityDto | null): boolean {
  if (!city?.viewportHigh || !city?.viewportLow) return false;
  return (
    coords.lat < city.viewportLow.lat ||
    coords.lat > city.viewportHigh.lat ||
    coords.lng < city.viewportLow.lng ||
    coords.lng > city.viewportHigh.lng
  );
}

export function useCitySwitchFlow(options: UseCitySwitchFlowOptions = {}) {
  const authStore = useAuthStore();
  const cityStore = useCityStore();
  const { city } = storeToRefs(cityStore);
  const sparkStore = useSparkStore();
  const { intents } = storeToRefs(sparkStore);
  const intentStore = useSparkIntentStore();
  const navigationStore = useNavigationStore();

  let pendingTarget: CitySwitchTarget | null = null;

  const handleConfirm = async () => {
    if (!pendingTarget?.coordinates) {
      close();
      return;
    }

    await cityStore.determineCity(pendingTarget.coordinates);

    if (!city.value?.id) {
      close();
      return;
    }

    await sparkStore.initializeAvailabilityHorizon(city.value.id);
    intentStore.clearIntentStore();
    await sparkStore.clear();
    await authStore.fetchUserInfo(true);
    await sparkStore.fetchIntents({ refreshExisting: true });

    const targetIntent = intents.value[0];
    if (targetIntent) {
      await sparkStore.findIntentAndGambitResults(targetIntent.id);
    }

    await navigationStore.refreshResults();
    close();
    navigationStore.navigateToCity();
    options.onComplete?.();
  };

  const handleCancel = async () => {
    pendingTarget = null;
    close();
    options.onCancel?.();
  };

  const { open, close, patchOptions } = useModal({
    component: ConfirmDialog,
    attrs: {
      title: '',
      prompt: '',
      confirmLabel: localizeWithValues('city_switch.confirm.yes', []),
      cancelLabel: localizeWithValues('city_switch.confirm.no', []),
      onConfirm: handleConfirm,
      onCancel: handleCancel,
    },
  });

  const openCitySwitch = (target: CitySwitchTarget) => {
    pendingTarget = target;

    const currentCityName = city.value?.name || '';

    patchOptions({
      attrs: {
        title: localizeWithValues('city_switch.confirm.title', [target.name]),
        prompt: localizeWithValues('city_switch.confirm.prompt', [target.name, currentCityName]),
        confirmLabel: localizeWithValues('city_switch.confirm.yes', []),
        cancelLabel: localizeWithValues('city_switch.confirm.no', []),
        onConfirm: handleConfirm,
        onCancel: handleCancel,
      },
    });

    open();
  };

  const openCitySwitchForLocation = (coordinates: GeoCoords) => {
    pendingTarget = { coordinates, name: '' };

    const currentCityName = city.value?.name || '';

    patchOptions({
      attrs: {
        title: localizeWithValues('city_switch.outside.title', []),
        prompt: localizeWithValues('city_switch.outside.prompt', [currentCityName]),
        confirmLabel: localizeWithValues('city_switch.confirm.yes', []),
        cancelLabel: localizeWithValues('city_switch.confirm.no', []),
        onConfirm: handleConfirm,
        onCancel: handleCancel,
      },
    });

    open();
  };

  return {
    openCitySwitch,
    openCitySwitchForLocation,
    isOutsideCity: (coords: GeoCoords) => isOutsidePlayingCity(coords, city.value),
  };
}
